import React from 'react'

import { Capacitor } from '@capacitor/core'
import { AppLauncher } from '@capacitor/app-launcher'

import { useNavigate } from 'react-router-dom'

import { Slide, Box, Stack, Button, Chip, Divider, Tabs, Tab, Typography } from '@mui/material'
import {
  KeyboardArrowDown,
  ElectricalServicesOutlined,
  BoltOutlined,
  AttachMoneyOutlined,
  MoreTimeOutlined,
  PaidOutlined,
} from '@mui/icons-material'

import ConnectorIcons from '@/components/ConnectorIcons'
import DragCarouselTrack from '@/components/DragCarouselTrack'

import { StationModalProps } from './types'
import { numericUtils } from '@/utils/numeric'
import { connectorUtils } from '@/utils/connector'
import { chargingPointStatusUtils } from '@/utils/chargingPointStatus'
import { useDragCarousel } from '@/hooks/useDragCarousel'

const MANY_CHARGING_POINTS_THRESHOLD = 3
const MANY_CONNECTOR_TYPES_THRESHOLD = 2

const showsConnectorLabels = (connectorTypes: string[]): boolean => {
  const distinctTypes = Array.from(new Set(connectorTypes))

  if (distinctTypes.length <= MANY_CONNECTOR_TYPES_THRESHOLD) return true

  return !distinctTypes.some(connectorUtils.hasIcon)
}

const StationModal: React.FC<StationModalProps> = ({ station, onClose }) => {
  const navigate = useNavigate()

  const statusChipColor = station ? chargingPointStatusUtils.getChipColor(station.status) : undefined
  const status = station ? chargingPointStatusUtils.getLabel(station.status) : 'Desconhecido'

  const chargingPoints = station?.charging_points ?? []
  const hasManyChargingPoints = chargingPoints.length > MANY_CHARGING_POINTS_THRESHOLD
  const showsStationConnectorIcons = !hasManyChargingPoints && chargingPoints.length > 1
  const availableChargingPointsCount = chargingPoints.filter(
    (chargingPoint) => chargingPointStatusUtils.getBucket(chargingPoint.status) === 'AVAILABLE'
  ).length
  const showsAvailableCount = station?.status === 'AVAILABLE' && chargingPoints.length > 1

  const connectorTypesSet = new Set<string>()
  chargingPoints.forEach((chargingPoint) => {
    chargingPoint.charging_connectors.forEach((connector) => {
      connectorTypesSet.add(connector.connector_type)
    })
  })
  const connectorTypes = Array.from(connectorTypesSet)
  const connectorLabelsString = connectorTypes.map(connectorUtils.getLabel).join(', ') || 'Desconhecido'

  const powers = Array.from(new Set(chargingPoints.map((chargingPoint) => chargingPoint.power)))
  const powersString = numericUtils.formatDistinctValues(powers, (power) => `${power} kW`) || 'Desconhecida'

  const ratesPerKwh = Array.from(
    new Set(chargingPoints.map((chargingPoint) => chargingPoint.current_rate_per_kwh ?? chargingPoint.rate_per_kwh))
  ).filter((rate): rate is number => rate != null)
  const ratesPerKwhString =
    numericUtils.formatDistinctValues(ratesPerKwh, (rate) => numericUtils.formatNumberToBRLCurrency(rate)) ||
    'Desconhecido'

  const idleRates = Array.from(
    new Set(chargingPoints.map((chargingPoint) => chargingPoint.current_idle_rate ?? chargingPoint.idle_rate))
  )
  const idleRatesString = idleRates.length
    ? `${numericUtils.formatDistinctValues(idleRates, (rate) => numericUtils.formatNumberToBRLCurrency(rate))}/min`
    : 'Desconhecida'

  const hasActivationFee = chargingPoints.some(
    (chargingPoint) => (chargingPoint.current_activation_fee ?? chargingPoint.activation_fee) > 0
  )
  const activationFees = Array.from(
    new Set(chargingPoints.map((chargingPoint) => chargingPoint.current_activation_fee ?? chargingPoint.activation_fee))
  )
  const activationFeesString =
    numericUtils.formatDistinctValues(activationFees, (fee) => numericUtils.formatNumberToBRLCurrency(fee)) ||
    'Desconhecida'

  const {
    activeIndex,
    trackViewportRef,
    trackViewportWidth,
    goToPrev: handlePrevChargingPoint,
    goToNext: handleNextChargingPoint,
    goToIndex: handleSelectChargingPoint,
    handleTrackDragEnd,
  } = useDragCarousel(chargingPoints.length, station?.id)

  const handleOnClickViewDetails = () => {
    onClose()

    navigate(`/station/${station?.id}`)
  }

  const handleOnClickTraceRoute = async () => {
    const destination = `${station?.latitude},${station?.longitude}`

    let url: string
    if (Capacitor.getPlatform() === 'ios') {
      url = `maps://?daddr=${destination}`
    } else if (Capacitor.getPlatform() === 'android') {
      url = `geo:${destination}?q=${destination}`
    } else {
      url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`
    }

    try {
      await AppLauncher.openUrl({ url })
    } catch {
      window.open(url, '_blank')
    }
  }

  return (
    <Box
      position="fixed"
      sx={{
        bottom: '112px',
        left: '50%',
        transform: 'translateX(-50%)',
        paddingTop: 'env(safe-area-inset-top)',
        paddingX: '24px',
        width: '100%',
        zIndex: 1,
      }}
    >
      <Slide direction="up" in={!!station} mountOnEnter unmountOnExit>
        <Stack
          direction="column"
          borderRadius={4}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            boxShadow: 4,
            overflow: 'hidden',
            maxHeight: 'calc(100dvh - 112px - env(safe-area-inset-top) - 16px)',
          }}
        >
          <Button variant="text" color="primary" onClick={onClose} sx={{ flexShrink: 0 }}>
            <KeyboardArrowDown />
          </Button>
          <Divider sx={{ flexShrink: 0 }} />
          <Box sx={{ overflowY: 'auto', minHeight: 0 }}>
            <Stack direction="column" spacing={2} paddingY={1}>
              <Stack direction="column" spacing={1}>
                <Stack direction="column" paddingX={2} spacing={1}>
                  <Stack direction="column" alignItems="flex-start" sx={{ minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      textOverflow="ellipsis"
                      overflow="hidden"
                      whiteSpace="nowrap"
                      maxWidth="100%"
                    >
                      {station?.name}
                    </Typography>
                    {station?.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        textOverflow="ellipsis"
                        overflow="hidden"
                        whiteSpace="nowrap"
                        maxWidth="100%"
                      >
                        {station?.description}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      textOverflow="ellipsis"
                      overflow="hidden"
                      whiteSpace="nowrap"
                      maxWidth="100%"
                    >
                      {station?.address}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      textOverflow="ellipsis"
                      overflow="hidden"
                      whiteSpace="nowrap"
                      maxWidth="100%"
                    >
                      {station?.city} - {station?.state}, {station?.zip_code}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Chip
                      label={
                        showsAvailableCount
                          ? `${status} · ${availableChargingPointsCount}/${chargingPoints.length}`
                          : status
                      }
                      size="small"
                      color={statusChipColor}
                      sx={{ fontWeight: 700, flexShrink: 0 }}
                    />
                    {showsStationConnectorIcons && <ConnectorIcons connectorTypes={connectorTypes} />}
                  </Stack>
                </Stack>
                <Divider />

                {!hasManyChargingPoints && chargingPoints.length > 1 && (
                  <Tabs
                    value={activeIndex}
                    onChange={(_event, newValue: number) => handleSelectChargingPoint(newValue)}
                    variant="scrollable"
                    scrollButtons={false}
                    sx={{ minHeight: 36, paddingX: 2 }}
                  >
                    {chargingPoints.map((chargingPoint, index) => {
                      const tabBucket = chargingPointStatusUtils.getBucket(chargingPoint.status)
                      const tabColorValue = chargingPointStatusUtils.getColorValue(tabBucket)

                      return (
                        <Tab
                          key={chargingPoint.id}
                          value={index}
                          sx={{ minHeight: 36, textTransform: 'none', paddingX: 1.5 }}
                          label={
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: tabColorValue }} />
                              <Typography variant="body2" fontWeight="bold">
                                {chargingPoint.title}
                              </Typography>
                            </Stack>
                          }
                        />
                      )
                    })}
                  </Tabs>
                )}

                {hasManyChargingPoints && (
                  <Stack direction="column" alignItems="flex-start" paddingX={2}>
                    <Stack direction="row" alignItems="center" maxWidth="100%">
                      <ElectricalServicesOutlined />
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        textOverflow="ellipsis"
                        overflow="hidden"
                        whiteSpace="nowrap"
                      >
                        <b>{connectorTypes.length > 1 ? 'Conectores' : 'Conector'}:</b>
                        {showsConnectorLabels(connectorTypes) && ` ${connectorLabelsString}`}
                      </Typography>
                      <ConnectorIcons connectorTypes={connectorTypes} sx={{ marginLeft: 1 }} />
                    </Stack>

                    <Stack direction="row" alignItems="center" maxWidth="100%">
                      <BoltOutlined />
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        textOverflow="ellipsis"
                        overflow="hidden"
                        whiteSpace="nowrap"
                      >
                        <b>{powers.length > 1 ? 'Potências' : 'Potência'}:</b> {powersString}
                      </Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center" maxWidth="100%">
                      <AttachMoneyOutlined />
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        textOverflow="ellipsis"
                        overflow="hidden"
                        whiteSpace="nowrap"
                      >
                        <b>Preço (kWh):</b> {ratesPerKwhString}
                      </Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center" maxWidth="100%">
                      <MoreTimeOutlined />
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        textOverflow="ellipsis"
                        overflow="hidden"
                        whiteSpace="nowrap"
                      >
                        <b>Ociosidade:</b> {idleRatesString}
                      </Typography>
                    </Stack>

                    {hasActivationFee && (
                      <Stack direction="row" alignItems="center" maxWidth="100%">
                        <PaidOutlined />
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          textOverflow="ellipsis"
                          overflow="hidden"
                          whiteSpace="nowrap"
                        >
                          <b>Ativação:</b> {activationFeesString}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                )}

                {!hasManyChargingPoints && chargingPoints.length > 0 && (
                  <DragCarouselTrack
                    items={chargingPoints}
                    activeIndex={activeIndex}
                    trackViewportRef={trackViewportRef}
                    trackViewportWidth={trackViewportWidth}
                    onDragEnd={handleTrackDragEnd}
                    onClickPrev={handlePrevChargingPoint}
                    onClickNext={handleNextChargingPoint}
                    getItemKey={(chargingPoint) => chargingPoint.id}
                    sx={{ paddingX: 2 }}
                    arrowOffset={4}
                    arrowButtonSx={{
                      minWidth: 'auto',
                      padding: 0.5,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    }}
                    renderItem={(chargingPoint) => {
                      const bucket = chargingPointStatusUtils.getBucket(chargingPoint.status)
                      const bucketChipColor = chargingPointStatusUtils.getChipColor(bucket)
                      const bucketLabel = chargingPointStatusUtils.getLabel(bucket)
                      const itemConnectorTypes = chargingPoint.charging_connectors.map(
                        (connector) => connector.connector_type
                      )
                      const itemConnectorLabels = Array.from(new Set(itemConnectorTypes))
                        .map(connectorUtils.getLabel)
                        .join(', ')

                      return (
                        <Stack
                          direction="column"
                          alignItems="flex-start"
                          spacing={1}
                          padding={1.5}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.35)',
                            backdropFilter: 'blur(6px)',
                            borderRadius: 3,
                          }}
                        >
                          {chargingPoints.length > 1 && (
                            <Chip size="small" color={bucketChipColor} label={bucketLabel} sx={{ fontWeight: 600 }} />
                          )}

                          <Stack direction="row" alignItems="center" maxWidth="100%">
                            <ElectricalServicesOutlined />
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              textOverflow="ellipsis"
                              overflow="hidden"
                              whiteSpace="nowrap"
                            >
                              <b>{chargingPoint.charging_connectors.length > 1 ? 'Conectores' : 'Conector'}:</b>
                              {showsConnectorLabels(itemConnectorTypes) && ` ${itemConnectorLabels || 'Desconhecido'}`}
                            </Typography>
                            <ConnectorIcons connectorTypes={itemConnectorTypes} sx={{ marginLeft: 1 }} />
                          </Stack>

                          <Stack direction="row" alignItems="flex-start" maxWidth="100%">
                            <BoltOutlined />
                            <Typography variant="body1" color="text.secondary">
                              <b>Potência:</b> {chargingPoint.power} kW
                            </Typography>
                          </Stack>

                          <Stack direction="row" alignItems="flex-start" maxWidth="100%">
                            <AttachMoneyOutlined />
                            <Typography variant="body1" color="text.secondary">
                              <b>Preço (kWh):</b>{' '}
                              {numericUtils.formatNumberToBRLCurrency(
                                chargingPoint.current_rate_per_kwh ?? chargingPoint.rate_per_kwh
                              )}
                            </Typography>
                          </Stack>

                          <Stack direction="row" alignItems="flex-start" maxWidth="100%">
                            <MoreTimeOutlined />
                            <Typography variant="body1" color="text.secondary">
                              <b>Ociosidade:</b>{' '}
                              {numericUtils.formatNumberToBRLCurrency(
                                chargingPoint.current_idle_rate ?? chargingPoint.idle_rate
                              )}
                              /min
                            </Typography>
                          </Stack>

                          {(chargingPoint.current_activation_fee ?? chargingPoint.activation_fee) > 0 && (
                            <Stack direction="row" alignItems="flex-start" maxWidth="100%">
                              <PaidOutlined />
                              <Typography variant="body1" color="text.secondary">
                                <b>Ativação:</b>{' '}
                                {numericUtils.formatNumberToBRLCurrency(
                                  chargingPoint.current_activation_fee ?? chargingPoint.activation_fee
                                )}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      )
                    }}
                  />
                )}
              </Stack>
            </Stack>
          </Box>
          <Divider sx={{ flexShrink: 0 }} />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            padding={2}
            sx={{ flexShrink: 0 }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleOnClickViewDetails}
              disableElevation
              sx={{ borderRadius: 4 }}
            >
              <Typography variant="body2">Ver detalhes</Typography>
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleOnClickTraceRoute}
              disableElevation
              sx={{ borderRadius: 4 }}
            >
              <Typography variant="body2">Traçar rota</Typography>
            </Button>
          </Stack>
        </Stack>
      </Slide>
    </Box>
  )
}

export default StationModal
